/**
 * StatusChangeConfirmationModal Component
 * Shows summary of status changes and allows users to opt out of email notifications
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Typography,
  List,
  Checkbox,
  Divider,
  Alert,
  Space,
  Tag,
  Button,
} from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { Text, Title } = Typography;

const StatusChangeConfirmationModal = ({
  open,
  statusChanges, // { orderId: { oldStatus, newStatus, order, customerEmail, customerName } }
  onConfirm, // (emailPreferences, options?) => void - emailPreferences: { orderId: boolean }; options: { includeReviewRequest }
  onCancel,
  loading = false,
}) => {
  const [emailPreferences, setEmailPreferences] = useState({});
  const [includeReviewRequest, setIncludeReviewRequest] = useState(false);

  // Initialize email preferences - all checked by default
  useEffect(() => {
    if (open && statusChanges) {
      const prefs = {};
      Object.keys(statusChanges).forEach((orderId) => {
        const change = statusChanges[orderId];
        // Only enable email if customer has email and status change warrants email
        prefs[orderId] = change.customerEmail ? true : false;
      });
      setEmailPreferences(prefs);
      setIncludeReviewRequest(false);
    }
  }, [open, statusChanges]);

  const handleToggleEmail = (orderId) => {
    setEmailPreferences((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleConfirm = () => {
    logInfo("StatusChangeConfirmationModal: Confirming with preferences", {
      emailPreferences,
      includeReviewRequest,
      statusChangesCount: Object.keys(statusChanges).length,
    });
    if (onConfirm) {
      onConfirm(emailPreferences, { includeReviewRequest });
    }
  };

  const handleCancel = () => {
    setEmailPreferences({});
    if (onCancel) {
      onCancel();
    }
  };

  if (!statusChanges || Object.keys(statusChanges).length === 0) {
    return null;
  }

  const statusChangeEntries = Object.entries(statusChanges);
  const ordersWithEmail = statusChangeEntries.filter(
    ([_, change]) => change.customerEmail
  ).length;
  const totalOrders = statusChangeEntries.length;
  const hasCompletedTransition = statusChangeEntries.some(
    ([_, change]) =>
      String(change.newStatus || "").toLowerCase() === "completed"
  );
  const anyEmailSelected = Object.values(emailPreferences).some(Boolean);

  const getStatusColor = (status) => {
    const colors = {
      pending: "default",
      booked: "blue",
      in_progress: "processing",
      completed: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined style={{ color: "#1890ff" }} />
          <Title level={4} style={{ margin: 0 }}>
            Confirm Status Changes
          </Title>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      <Alert
        message="Review the actions that will be taken"
        description={`${totalOrders} order${totalOrders > 1 ? "s" : ""} will be updated. Review the details below and uncheck any emails you don't want to send.`}
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Divider orientation="left">Status Changes</Divider>

      <List
        dataSource={statusChangeEntries}
        renderItem={([orderId, change]) => {
          const hasEmail = !!change.customerEmail;
          const willSendEmail = emailPreferences[orderId] && hasEmail;

          return (
            <List.Item>
              <div style={{ width: "100%" }}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <div>
                    <Text strong>Order: {orderId}</Text>
                    {change.customerName && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        ({change.customerName})
                      </Text>
                    )}
                  </div>

                  <div>
                    <Tag color={getStatusColor(change.oldStatus)}>
                      {change.oldStatus?.toUpperCase().replace("_", " ") || "UNKNOWN"}
                    </Tag>
                    <Text> → </Text>
                    <Tag color={getStatusColor(change.newStatus)}>
                      {change.newStatus?.toUpperCase().replace("_", " ") || "UNKNOWN"}
                    </Tag>
                  </div>

                  {hasEmail && (
                    <div style={{ marginTop: 8 }}>
                      <Checkbox
                        checked={emailPreferences[orderId]}
                        onChange={() => handleToggleEmail(orderId)}
                      >
                        <Space>
                          <MailOutlined />
                          <Text>
                            Send email notification to{" "}
                            <Text strong>{change.customerEmail}</Text>
                          </Text>
                        </Space>
                      </Checkbox>
                    </div>
                  )}

                  {!hasEmail && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" italic>
                        <InfoCircleOutlined /> No email address available for this customer
                      </Text>
                    </div>
                  )}
                </Space>
              </div>
            </List.Item>
          );
        }}
      />

      {hasCompletedTransition && (
        <>
          <Divider orientation="left">Review request</Divider>
          <Checkbox
            checked={includeReviewRequest}
            disabled={!anyEmailSelected}
            onChange={(e) => setIncludeReviewRequest(e.target.checked)}
            data-testid="include-review-request-checkbox"
          >
            <Text strong>Send review request?</Text>
          </Checkbox>
          <div style={{ margin: "8px 0 0 24px" }}>
            <Text type="secondary">
              Adds Facebook and Google review links to the completion email.
              Ignored if the completion email is unchecked above.
            </Text>
          </div>
        </>
      )}

      <Divider />

      <Alert
        message="Summary"
        description={
          <Space direction="vertical" size="small">
            <Text>
              • {totalOrders} order{totalOrders > 1 ? "s" : ""} will be updated
            </Text>
            <Text>
              • {Object.values(emailPreferences).filter(Boolean).length} email
              {Object.values(emailPreferences).filter(Boolean).length !== 1 ? "s" : ""}{" "}
              will be sent
            </Text>
            <Text>
              • {ordersWithEmail - Object.values(emailPreferences).filter(Boolean).length}{" "}
              email{ordersWithEmail - Object.values(emailPreferences).filter(Boolean).length !== 1 ? "s" : ""}{" "}
              will be skipped
            </Text>
          </Space>
        }
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          loading={loading}
          icon={<CheckCircleOutlined />}
        >
          Confirm & Update
        </Button>
      </div>
    </Modal>
  );
};

export default StatusChangeConfirmationModal;

